(function ($, window, document) {
    'use strict';

    /**
     * Configuration object that defines system-wide settings for the WayGen system status interface.
     *
     * @property {string} api - The URL or endpoint to fetch system health data. Defaults to '/health'
     *                          if not provided by `window.WayGenStatus.api`.
     * @property {number} refreshSec - The interval, in seconds, to refresh data from the API. Defaults
     *                                 to 60 seconds if not provided by `window.WayGenStatus.refreshSec`.
     * @property {Object} i18n - Translations and labels used for the system status interface. Defaults
     *                           to predefined values, with the ability to override them via
     *                           `window.WayGenStatus.i18n`.
     */
    const CFG = {
        api: window.WayGenStatus?.api || '/health',
        refreshSec: Number(window.WayGenStatus?.refreshSec || 60),
        i18n: Object.assign({
            pageTitle: 'System Status',
            pageSubtitle: 'Live service health and worker utilization.',
            service: 'Service',
            name: 'Name',
            status: 'Status',
            lastUpdate: 'Last update',
            coverage: 'Route Coverage',
            capacity: 'System Capacity',
            busy: 'Busy',
            free: 'Free',
            total: 'Total',
            loading: 'Loading…',
            ok: 'OK',
            degraded: 'Degraded',
            down: 'Down',
            errorLoading: 'ERROR'
        }, window.WayGenStatus?.i18n || {})
    };

    const $name = $('#svc-name');
    const $version = $('#svc-version');
    const $serviceStatus = $('#svc-status');
    const $serviceTs = $('#svc-ts');
    const $dbStatus = $('#db-status');
    const $dbTs = $('#db-ts');
    const $coverageStatus = $('#coverage-status');
    const $busy = $('#capacity-busy');
    const $free = $('#capacity-free');
    const $total = $('#capacity-total');
    const $bar = $('#capacity-bar');

    /**
     * Updates the content and class of a badge element.
     *
     * @param {jQuery} $el - The jQuery object representing the element to modify.
     * @param {string} text - The text to set as the badge content.
     * @param {string} cls - The CSS class to apply to the badge for styling.
     * @return {void} No return value.
     */
    function setBadge($el, text, cls) {
        $el.text(text).attr('class', 'status-badge ' + cls);
    }

    /**
     * Formats an ISO date string into a localized string representation of the date and time.
     * If the input is null, undefined, or an invalid date, it returns a fallback string.
     *
     * @param {string} iso - The ISO date string to format.
     * @return {string} A localized string representation of the date and time, or a fallback string if input is invalid.
     */
    function fmtTime(iso) {
        if (!iso) return '—';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) {
            return iso;
        }
        return d.toLocaleString();
    }

    /**
     * Updates the UI elements to reflect an error state by resetting text, statuses,
     * and other visual components to default or error-indicative values.
     *
     * @return {void} No return value.
     */
    function setError() {
        $name.text('—');
        $version.text('—');
        $serviceTs.text('—');
        $dbTs.text('—');

        setBadge($serviceStatus, CFG.i18n.errorLoading, 'status-badge--down');
        setBadge($dbStatus, CFG.i18n.errorLoading, 'status-badge--down');
        setBadge($coverageStatus, CFG.i18n.errorLoading, 'status-badge--down');

        $busy.text('0');
        $free.text('0');
        $total.text('0');
        $bar.css('width', '0%');
    }

    /**
     * Processes and applies the data from the provided JSON object to update UI elements such as service details,
     * timestamps, statuses, coverage, and capacity.
     *
     * @param {Object} json - The JSON object containing data to be processed and applied.
     * @param {string} [json.timestamp] - The timestamp associated with the data.
     * @param {string} [json.service] - The name of the service.
     * @param {string} [json.version] - The version of the service.
     * @param {string} [json.status] - The operational status of the service. Expected values: 'ok', 'degraded', 'down'.
     * @param {boolean} [json.db] - The status of the database connection.
     * @param {Array<string>} [json.countries] - A list of countries indicating service coverage.
     * @param {Object} [json.capacity] - Information about capacity details.
     * @param {number} [json.capacity.busy] - The number of busy units.
     * @param {number} [json.capacity.total] - The total number of units.
     * @param {number} [json.capacity.free] - The number of free units. Defaults to the difference between total and busy.
     *
     * @return {void} No return value.
     */
    function applyData(json) {
        const timestamp = json.timestamp ? fmtTime(json.timestamp) : '—';

        $name.text(json.service || '—');
        $version.text(json.version || '—');
        $serviceTs.text(timestamp);
        $dbTs.text(timestamp);

        // service status
        const serviceStatus = String(json.status || '').toLowerCase();
        if (serviceStatus === 'ok') {
            setBadge($serviceStatus, CFG.i18n.ok, 'status-badge--ok');
        } else if (serviceStatus === 'degraded') {
            setBadge($serviceStatus, CFG.i18n.degraded, 'status-badge--warn');
        } else if (serviceStatus === 'down') {
            setBadge($serviceStatus, CFG.i18n.down, 'status-badge--down');
        } else {
            setBadge($serviceStatus, CFG.i18n.loading, 'status-badge--loading');
        }

        // DB
        const dbOk = json.db === true;
        if (dbOk) {
            setBadge($dbStatus, CFG.i18n.ok, 'status-badge--ok');
        } else {
            setBadge($dbStatus, CFG.i18n.down, 'status-badge--down');
        }

        // coverage
        const countries = Array.isArray(json.countries) ? json.countries : [];
        if (countries.length > 0) {
            setBadge($coverageStatus, CFG.i18n.ok, 'status-badge--ok');
        } else {
            setBadge($coverageStatus, CFG.i18n.free, 'status-badge--warn');
        }

        // capacity
        const busy = Number(json.capacity?.busy ?? 0);
        const total = Number(json.capacity?.total ?? 0);
        const free = Number(json.capacity?.free ?? Math.max(0, total - busy));

        $busy.text(String(busy));
        $free.text(String(free));
        $total.text(String(total));

        const pct = total > 0 ? Math.min(100, Math.max(0, Math.round((busy / total) * 100))) : 0;

        $bar.css('width', pct + '%');
    }

    /**
     * Fetches the current status from the API. If a previous AJAX request is in progress, it aborts the request.
     *
     * @param {Object|null} jqPrev - The previous jQuery AJAX object. If it is non-null and still in progress, it will be aborted.
     * @return {Object} A jQuery Promise object representing the current AJAX request.
     */
    function fetchStatus(jqPrev) {
        if (jqPrev && jqPrev.readyState !== 4) {
            try {
                jqPrev.abort();
            } catch (_) {
            }
        }
        return $.ajax({
            url: CFG.api,
            method: 'GET',
            dataType: 'json',
            xhrFields: {withCredentials: true}
        });
    }

    // init
    setBadge($serviceStatus, CFG.i18n.loading, 'status-badge--loading');
    setBadge($dbStatus, CFG.i18n.loading, 'status-badge--loading');
    setBadge($coverageStatus, CFG.i18n.loading, 'status-badge--loading');

    let timer = null;
    let jqXHR = null;

    /**
     * Schedules the next execution of the `tick` function based on a configurable refresh interval.
     * If the configured interval is less than 3 seconds, a default delay of 3 seconds is used.
     *
     * @return {void} This function does not return a value.
     */
    function scheduleNext() {
        timer = setTimeout(tick, Math.max(3000, CFG.refreshSec * 1000));
    }

    /**
     * Executes a tick operation that performs an asynchronous request to fetch status,
     * processes the retrieved data, handles errors, and schedules the next operation.
     *
     * @return {void} Does not return a value.
     */
    function tick() {
        jqXHR = fetchStatus(jqXHR)
            .done(function (json) {
                applyData(json);
            })
            .fail(function () {
                setError();
            })
            .always(function () {
                scheduleNext();
            });
    }

    $(function () {
        tick();
    });

    $(window).on('beforeunload', function () {
        if (jqXHR && jqXHR.readyState !== 4) {
            try {
                jqXHR.abort();
            } catch (_) {
            }
        }
        if (timer) clearTimeout(timer);
    });

})(jQuery, window, document);
